import { OnInit, HostListener, Directive } from "@angular/core";
import { Location } from "@angular/common";
import { UntypedFormGroup, UntypedFormBuilder } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import * as fromRoot from "../../../reducers";
import { Store } from "@ngrx/store";
import { Observable, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import * as layout from "./../../../core/layout/shared/layout.action";

import { ChangeStatusCommand } from "../models/changeStatusCommand";
import { AssetService } from "../../services/asset.service";
import { AssetCommand } from "../models/assetCommand";
import { AssetDTO } from "../dto-models/assetDTO.model";

import { ServiceSummaryDTO } from "../dto-models/serviceSummaryDTO.model";
import { ActionDropDownDTO } from "../dto-models/actionDropDownDTO.model";
import { AESEncryptDecryptService } from "../services/aes-encrypt-decrypt.service";
import { AuditMetaDataDTO } from "../dto-models/auditMetadataDTO.model";
@Directive()
export class AssetFormComponentBase implements OnInit {
  tabGroupHeight: string;
  headerAndFooterHeight = 250;

  isLoading = false;
  saveAndClose = false;

  assetId: any;
  asset: AssetDTO;
  loadedAsset: Observable<AssetDTO>;

  assetForm: UntypedFormGroup;
  public formResources = <any>{};
  public validationMessages = <any>{};

  defaultLoadMessage: string;
  defaultErrorMessages: string;
  pageErrors = "";

  auditMetadata: AuditMetaDataDTO;
  backgroundTaskRunning$: Observable<boolean>;

  constructor(
    private _store: Store<fromRoot.State>,
    private _formBuilder: UntypedFormBuilder,
    private _assetService: AssetService,
    private _route: ActivatedRoute,
    private _router: Router,
    public translate: TranslateService,
    public _snackbar: MatSnackBar,
    private _location: Location,
    private _aesService: AESEncryptDecryptService,
  ) {
    this.createForm();

    this.defaultLoadMessage = translate.instant(
      "MESSAGES.SNACKBAR_LOADMESSAGE",
    );
    this.defaultErrorMessages = translate.instant(
      "MESSAGES.SNACKBAR_ERRORMESSAGES",
    );
    this.validationMessages = {
      required: translate.instant("MESSAGES.VALIDATION_MESSAGES.REQUIRED"),
      email: translate.instant("MESSAGES.VALIDATION_MESSAGES.EMAIL"),
      max: translate.instant("MESSAGES.VALIDATION_MESSAGES.MAX"),
      maxlength: translate.instant("MESSAGES.VALIDATION_MESSAGES.MAXLENGTH"),
      min: translate.instant("MESSAGES.VALIDATION_MESSAGES.MIN"),
      minlength: translate.instant("MESSAGES.VALIDATION_MESSAGES.MINLENGTH"),
      pattern: translate.instant("MESSAGES.VALIDATION_MESSAGES.PATTERN"),
      matDatepickerMin: translate.instant(
        "MESSAGES.VALIDATION_MESSAGES.MINDATE",
      ),
      matDatepickerMax: translate.instant(
        "MESSAGES.VALIDATION_MESSAGES.MAXDATE",
      ),
      matDatepickerParse: translate.instant(
        "MESSAGES.VALIDATION_MESSAGES.PARSEDATE",
      ),
    };

    this.assetId = this._route.snapshot.queryParams.id;
    this.asset = <AssetDTO>{
      services: <ServiceSummaryDTO[]>[],
      actions: <ActionDropDownDTO[]>[],
    };

    this.formResources = {
      buildingId: {
        fieldName: translate.instant("ASSET.FORM.BUILDINGID"),
        errorMessage: "",
      },
      buildingName: {
        fieldName: translate.instant("ASSET.FORM.BUILDINGNAME"),
        errorMessage: "",
      },
      name: {
        fieldName: translate.instant("ASSET.FORM.NAME"),
        errorMessage: "",
      },
      assetRef: {
        fieldName: translate.instant("ASSET.FORM.ASSETREF"),
        errorMessage: "",
      },
      locationId: {
        fieldName: translate.instant("ASSET.FORM.LOCATIONID"),
        errorMessage: "",
      },
      locationName: {
        fieldName: translate.instant("ASSET.FORM.LOCATIONNAME"),
        errorMessage: "",
      },
      zoneId: {
        fieldName: translate.instant("ASSET.FORM.ZONEID"),
        errorMessage: "",
      },
      zoneName: {
        fieldName: translate.instant("ASSET.FORM.ZONENAME"),
        errorMessage: "",
      },
      locationTypeId: {
        fieldName: translate.instant("ASSET.FORM.LOCATIONTYPEID"),
        errorMessage: "",
      },
      locationTypeName: {
        fieldName: translate.instant("ASSET.FORM.LOCATIONTYPENAME"),
        errorMessage: "",
      },
      documentId: {
        fieldName: translate.instant("ASSET.FORM.DOCUMENTID"),
        errorMessage: "",
      },
      document: {
        fieldName: translate.instant("ASSET.FORM.DOCUMENT"),
        errorMessage: "",
      },
      id: { fieldName: translate.instant("ASSET.FORM.ID"), errorMessage: "" },
      createdBy: {
        fieldName: translate.instant("ASSET.FORM.CREATEDBY"),
        errorMessage: "",
      },
      createdDate: {
        fieldName: translate.instant("ASSET.FORM.CREATEDDATE"),
        errorMessage: "",
      },
      updatedBy: {
        fieldName: translate.instant("ASSET.FORM.UPDATEDBY"),
        errorMessage: "",
      },
      updatedDate: {
        fieldName: translate.instant("ASSET.FORM.UPDATEDDATE"),
        errorMessage: "",
      },
      rowVersion: {
        fieldName: translate.instant("ASSET.FORM.ROWVERSION"),
        errorMessage: "",
      },
      statusId: {
        fieldName: translate.instant("ASSET.FORM.STATUSID"),
        errorMessage: "",
      },
      status: {
        fieldName: translate.instant("ASSET.FORM.STATUS"),
        errorMessage: "",
      },
    };
  }

  // These methods have to be implemented in the main Component
  protected loadAdditionalData() {}
  protected resetAdditionalProperties() {}
  protected setServicesList(data: ServiceSummaryDTO[]) {}
  protected setActionsList(data: ActionDropDownDTO[]) {}
  protected prepareSaveAsset(): AssetCommand {
    return null;
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.tabGroupHeight = `${
      event.target.innerHeight - this.headerAndFooterHeight
    }px`;
  }

  createForm() {
    this.assetForm = this._formBuilder.group({
      buildingId: null,
      buildingName: null,
      name: null,
      assetRef: null,
      assetComment: null,
      locationId: null,
      locationName: null,
      zoneId: null,
      zoneName: null,
      locationTypeId: null,
      locationTypeName: null,
      services: this._formBuilder.array([]),
      documentId: null,
      document: null,
      id: null,
      createdBy: null,
      createdDate: null,
      updatedBy: null,
      updatedDate: null,
      rowVersion: null,
      statusId: null,
      status: null,
      actions: this._formBuilder.array([]),
    });
  }

  ngOnInit(): void {
    this.backgroundTaskRunning$ = this._store.select(
      fromRoot.getBackgroundTaskRunning,
    );

    this.tabGroupHeight = `${
      window.innerHeight - this.headerAndFooterHeight
    }px`;

    if (this.assetId) {
      this.getAsset();
    }
  }

  revalidateForm() {
    this.assetForm.markAllAsTouched();
    Object.keys(this.assetForm.controls).forEach((key) => {
      this.assetForm.get(key).updateValueAndValidity();
    });
  }

  cancel() {
    this._router.navigateByUrl(`asset`);
  }

  getAsset(): any {
    this.isLoading = true;
    this.loadedAsset = this._assetService.getDataById(this.assetId);

    this.loadedAsset.subscribe((r) => {
      this.asset = r;

      this.auditMetadata = {
        createdByName: r.createdBy,
        createdOn: r.createdDate,
        updatedByName: r.updatedBy,
        updatedOn: r.updatedDate,
      };

      if (r.services) {
        this.setServicesList(r.services);
      }
      if (r.actions) {
        this.setActionsList(r.actions);
      }
      this.loadAdditionalData();

      this.rebuildForm();
      this.snackBarHandler(this.defaultLoadMessage);

      this.assetForm.markAsPristine();

      this.isLoading = false;
      this._store.dispatch(new layout.StopBackgroundTask());
    });
  }

  rebuildForm(): any {
    this.assetForm.reset({
      buildingId: this.asset.buildingId,
      buildingName: this.asset.buildingName,
      name: this.asset.name,
      assetRef: this.asset.assetRef,
      assetComment: this.asset.assetComment,
      locationId: this.asset.locationId,
      locationName: this.asset.locationName,
      zoneId: this.asset.zoneId,
      zoneName: this.asset.zoneName,
      locationTypeId: this.asset.locationTypeId,
      locationTypeName: this.asset.locationTypeName,
      services: this.asset.services,
      documentId: this.asset.documentId,
      document: this.asset.document,
      id: this.asset.id,
      createdBy: this.asset.createdBy,
      createdDate: this.asset.createdDate,
      updatedBy: this.asset.updatedBy,
      updatedDate: this.asset.updatedDate,
      rowVersion: this.asset.rowVersion,
      statusId: this.asset.statusId,
      status: this.asset.status,
    });

    this.resetAdditionalProperties();

    if (this.asset.services) {
      this.setServicesList(this.asset.services);
    }
    if (this.asset.actions) {
      this.setActionsList(this.asset.actions);
    }
  }

  setSaveAndClose(type: boolean) {
    this.saveAndClose = type;
  }

  onSubmit() {
    this.revalidateForm();

    if (this.assetForm.invalid) {
      this.prepareSaveAsset();
    } else {
      this.pageErrors = "";

      const saveAsset = this.prepareSaveAsset();
      this._store.dispatch(new layout.StartBackgroundTask());

      if (saveAsset != null) {
        if (this.assetId) {
          // update
          this._assetService
            .updateAsset(saveAsset)
            .pipe(
              catchError((err) => {
                return this.translateError(err);
              }),
              finalize(() => {
                this._store.dispatch(new layout.StopBackgroundTask());
              })
            )
            .subscribe((r) => {
              if (r) {
                if (this.saveAndClose) {
                  this._router.navigateByUrl(`asset`);
                } else {
                  this.getAsset();
                }
              }
            });
        } else {
          // create
          this._assetService
            .createAsset(saveAsset)
            .pipe(
              catchError((err) => {
                return this.translateError(err);
              }),
              finalize(() => {
                this._store.dispatch(new layout.StopBackgroundTask());
              })
            )
            .subscribe((r) => {
              if (r) {
                this.assetId = btoa(this._aesService.encrypt(r));
                if (r && this.saveAndClose) {
                  this._router.navigateByUrl(`asset`);
                } else {
                  this._router.navigateByUrl(`asset/form?id=${this.assetId}`);
                  this.getAsset();
                }
              }
            });
        }
      } else {
        this._store.dispatch(new layout.StopBackgroundTask());
      }
    }
  }

  changeStatus(assetId: number, newStatus: number) {
    const command: ChangeStatusCommand = {
      id: assetId,
      newStatus: newStatus,
    };

    this._assetService.changeStatus(command).subscribe((r) => {
      if (r) {
        this.getAsset();
      }
    });
  }

  // get all values of the formGroup, loop over them
  // then mark each field as touched
  public markFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls)
      .map((key) => formGroup.controls[key])
      .forEach((control) => {
        if (control.value) {
          control.markAsTouched();
        } else {
          control.markAsUntouched();
        }
      });
  }

  private translateError(error: any) {
    if (error && typeof error === "string") {
      const lookup = "ERRORS.CONSTRAINTS." + error.toUpperCase();

      let message: string;
      message = this.translate.instant(lookup);
      if (!message) {
        message = this.translate.instant("ERRORS.DEFAULT");
      }
      this._snackbar.open(
        `${message}`,
        this.translate.instant("ACTIONS.CLOSE"),
        {
          duration: 1000,
          panelClass: "snackbar-error",
        },
      );
    }
    return throwError(() => error);
  }

  snackBarHandler(message: string) {
    if (this.pageErrors.length > 0) {
      if (!this._snackbar._openedSnackBarRef) {
        this._snackbar.open(`${message} ${this.pageErrors}`, "Ok", {
          duration: this.pageErrors.split("\n").length * 1000,
          panelClass: "snackbar-error",
        });
      }
    } else {
      this._snackbar.open(
        `${this.translate.instant("ASSET.TITLE")} ${message}`,
        this.translate.instant("ACTIONS.CLOSE"),
        {
          duration: 1000,
          panelClass: "snackbar-info",
        },
      );
    }
  }
}
